import { Component, OnInit, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { LoadingService } from 'src/app/services/loading.service';
import { OrderComercioService, OrdenComercioAdminRow } from 'src/app/services/order-comercio.service';
import { EstadoOrden } from 'src/app/models/EstadoOrden';
import { claseEstadoOrden } from 'src/app/shared/estado-orden.util';

@Component({
    selector: 'app-more-ordenes-comercio',
    templateUrl: './more-ordenes-comercio.component.html',
    styleUrls: ['./more-ordenes-comercio.component.css'],
    standalone: false
})
export class MoreOrdenesComercioComponent implements OnInit {
  private _authService = inject(AuthService);
  private _orderComercioService = inject(OrderComercioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);

  ordenes = signal<OrdenComercioAdminRow[]>([]);
  estadosOrdenes = signal<EstadoOrden[]>([]);
  total = signal<number>(0);
  page = signal<number>(1);
  readonly limit = 20;
  searchTerm = signal<string>('');
  filtroEstado = signal<number | null>(null);
  error = signal<string>('');

  private searchTerm$ = new Subject<string>();

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.total() / this.limit));
  }

  async ngOnInit(): Promise<void> {
    document.documentElement.scrollTop = 0;

    this.searchTerm$.pipe(
      debounceTime(350),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchTerm.set(term);
      this.page.set(1);
      this.cargar();
    });

    try {
      await this._loadingService.run(async () => {
        await this._authService.ensureSession();
        this.estadosOrdenes.set(await firstValueFrom(this._orderComercioService.getEstadosOrdenes()));
        await this.cargar();
      });
    } catch (e: any) {
      console.error(e);
      this.error.set(e?.message || 'No se pudo cargar tu cuenta.');
      this.router.navigate(['/sign-in']);
    }
  }

  onSearchInput(value: string): void {
    this.searchTerm$.next(value);
  }

  onFiltroEstadoChange(value: string): void {
    this.filtroEstado.set(value ? Number(value) : null);
    this.page.set(1);
    this.cargar();
  }

  async cargar(): Promise<void> {
    this.error.set('');
    try {
      await this._loadingService.run(async () => {
        const res = await firstValueFrom(this._orderComercioService.buscarAdmin({
          page: this.page(),
          limit: this.limit,
          search: this.searchTerm().trim() || undefined,
          id_estado_orden: this.filtroEstado(),
        }));
        this.ordenes.set(res.rows ?? []);
        this.total.set(res.total ?? 0);
      });
    } catch (e) {
      console.error(e);
      this.error.set('No se pudieron cargar las órdenes.');
    }
  }

  paginaAnterior(): void {
    if (this.page() <= 1) return;
    this.page.update(p => p - 1);
    this.cargar();
  }

  paginaSiguiente(): void {
    if (this.page() >= this.totalPaginas) return;
    this.page.update(p => p + 1);
    this.cargar();
  }

  fecha(o: OrdenComercioAdminRow): string {
    if (!o.d_date_create) return 'Sin fecha';
    try {
      return new Date(o.d_date_create).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return 'Sin fecha';
    }
  }

  // Expuesto para usar directo en el template
  readonly claseEstadoOrden = claseEstadoOrden;

  back(): void {
    this._location.back();
  }
}
